<template>
  <div class="my-area__box">
    <div class="my-area__box-header">
      <font-awesome-icon :icon="icon ? ['fas', icon] : ['fas', 'info-circle']" />
    </div>
    <b-card v-if="module" class="my-area__box-body" :style="`backgroundColor:${replCssVar(backgroundColor)}`">

      <p class="my-area__box-title" :class="`my-area__box-title-${moduleTitle}`"
        :style="`color:${replCssVar(titleColor)}`" v-html="$t(moduleTitle)" />

      <b-card-text><span v-html="$t(moduleDescription)" /></b-card-text>
      <b-button @click="goToModule(module)" :style="buttonStyles">
        <span class="my-area__button-text">
          <span v-html="$t(moduleButtonText)" /><span>{{ ' ' }}</span><font-awesome-icon :icon="['fas', 'arrow-right']" />
        </span>
      </b-button>
    </b-card>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import theme from '~/utilities/theme'
import utils from '../../utilities'
import trackingApi from '~/api/tracking'
import bannerControl from '~/utilities/bannerControl'
import accessControl from '~/utilities/accessControl'
import EventBus from '~/states/event-bus'
import userState from '~/states/user'
import appContainerMixins from "../../mixins/appContainerMixins"

const moduleNames = {
  documents: {
    title: 'VMX.my_area_box_documents_title',
    buttonText: 'VMX.my_area_box_documents_button',
    description: 'VMX.my_area_box_documents_description',
  },
  submission: {
    title: 'VMX.my_area_box_submission_title',
    buttonText: 'VMX.my_area_box_submission_button',
    description: 'VMX.my_area_box_submission_description',
  },
  evaluation: {
    title: 'VMX.my_area_box_evaluation_title',
    buttonText: 'VMX.my_area_box_evaluation_button',
    description: 'VMX.my_area_box_evaluation_description',
  },
  program: {
    title: 'VMX.my_area_box_program_title',
    buttonText: 'VMX.my_area_box_program_button',
    description: 'VMX.my_area_box_program_description',
  },
  helpdesk: {
    title: 'VMX.my_area_box_helpdesk_title',
    buttonText: 'VMX.my_area_box_helpdesk_button',
    description: 'VMX.my_area_box_helpdesk_description',
  },
  profile: {
    title: 'VMX.my_area_box_profile_title',
    buttonText: 'VMX.my_area_box_profile_button',
    description: 'VMX.my_area_box_profile_description',
  },
  groups: {
    title: 'VMX.my_area_box_groups_title',
    buttonText: 'VMX.my_area_box_groups_button',
    description: 'VMX.my_area_box_groups_description',
  },
  inbox: {
    title: 'VMX.my_area_box_inbox_title',
    buttonText: 'VMX.my_area_box_inbox_button',
    description: 'VMX.my_area_box_inbox_description',
  },
  networking_settings: {
    title: 'VMX.my_area_box_networking_settings_title',
    buttonText: 'VMX.my_area_box_networking_settings_button',
    description: 'VMX.my_area_box_networking_settings_description',
  },
  notifications: {
    title: 'VMX.my_area_box_notifications_title',
    buttonText: 'VMX.my_area_box_notifications_button',
    description: 'VMX.my_area_box_notifications_description',
  },
  abstractsubmission: {
    title: 'VMXEventDashboard.VMXAbstractSubmission',
    buttonText: 'VMXEventDashboard.VMXSessionSubmission_Button',
  },
  sessionsubmission: {
    title: 'VMXEventDashboard.VMXSessionSubmission',
    buttonText: 'VMXEventDashboard.VMXSessionSubmission_Button',
  },
  messenger: {
    title: 'VMX.my_area_box_messenger_title',
    description: 'VMX.my_area_box_messenger_button',
    buttonText: 'VMX.my_area_box_messenger_description',
  },
  messengersettings: {
    title: 'VMX.my_area_box_messengersettings_title',
    description: 'VMX.my_area_box_messengersettings_button',
    buttonText: 'VMX.my_area_box_messengersettings_description',
  },
}

export default {
  name: 'MyAreaBox',
  inheritAttrs: false,
  data() {
    return {
      redirectToPagProgram: true,
    }
  },
  mixins: [
    appContainerMixins,
  ],
  components: {},
  props: {
    backgroundColor: {
      type: String,
      default: 'white',
    },
    icon: {
      type: String,
      default: null,
    },
    titleColor: {
      type: String,
      default: 'var(--primary)',
    },
    buttonBackgroundColor: {
      type: String,
      default: 'var(--primary)',
    },
    buttonTextColor: {
      type: String,
      default: 'var(--primary-contrast)',
    },
    module: {
      type: String,
      default: null,
    },
    link: {
      type: String,
      default: null,
    },
    title: {
      type: String,
      default: '',
    },
    description: {
      type: String,
      default: '',
    },
    buttonText: {
      type: String,
      default: '',
    },
    manageButtonText: {
      type: String,
      default: '',
    },
    moduleType: {
      type: String,
      default: '',
    },
    target: {
      type: String,
      default: '_blank',
    },
  },
  computed: {
     ...mapGetters({
      instance: 'instancesState/getInstance',
    }),
    isApp() {
      return localStorage.getItem('isApp') === 'true'
    },
    user() {
      return this.instance ? userState.getUser(this.instance) : null
    },
    moduleTitle() {
      const module = this.getModuleName(this.module)
      if (module && this.title === '') {
        return module.title 
      }

      if (this.title) return this.title
      if (this.moduleType === 'CustomService') return this.module
      return this.module
    },
    moduleDescription() {
      const module = this.getModuleName(this.module)
      if (module && this.description === '') {
        return module.description
      }
      return this.description
    },
    moduleButtonText() {
      const module = this.getModuleName(this.module)
      if (module && this.buttonText === '') {
        return module.buttonText
      }
      if (this.buttonText) return this.buttonText
      if (this.moduleType === 'CustomService') return this.module

      return this.module
    },
    buttonStyles() {
      return {
        color: this.replCssVar(this.buttonTextColor),
        backgroundColor: this.replCssVar(this.buttonBackgroundColor),
        borderColor: this.replCssVar(this.buttonBackgroundColor),
      }
    },
    redirectToPagPersonalProgram() {
      const c = this.$store.getters['config/configForKey']('app_config').configV2.defaultV2
      if ('redirectToPagPersonalProgram' in c) return c.redirectToPagPersonalProgram
      return true
    },
  },
  methods: {
    replCssVar(val) {
      return theme.replaceCssVar(val)
    },
    async goToModule(moduleName) {
      bannerControl.bannerDisplay(moduleName)

      if (moduleName === 'custom') {
        this.$router.push(utils.getRouteWithDefaultParams('MyAreaPage', { module: this.title.toLowerCase() }))
        this.tracking(this.title.toLowerCase())
      } else if (moduleName === 'helpdesk') {
        this.$router.push(utils.getRouteWithDefaultParams('ContactPage'))
      } else if (moduleName === 'program' && !window.jmeNetworking && this.redirectToPagPersonalProgram === true) {
        this.$router.push(utils.getRouteWithDefaultParams('PagPersonalProgrammePage'))
      } else if (moduleName === 'notifications') {
        this.$router.push(utils.getRouteWithDefaultParams('NotificationsPage'))
      } else if (moduleName === 'submission') {
        this.$router.push(utils.getRouteWithDefaultParams('SubmissionPage'))
      } else if (moduleName === 'registration') {
        this.$router.push(utils.getRouteWithDefaultParams('RegistrationPage'))
      } else if (moduleName === 'myprofile') {
        this.$router.push(utils.getRouteWithDefaultParams('UserFormPage'))
      } else if (moduleName === 'abstractsubmission') {
        this.$router.push(utils.getRouteWithDefaultParams('SubmissionPage', { module: 'abstracts' }))
      } else if (moduleName === 'sessionsubmission') {
        this.$router.push(utils.getRouteWithDefaultParams('SubmissionPage', { module: 'sessions' }))
      } else if (moduleName.includes('custom-module')) {
        if (this.link !== null) {
          if(utils.isObject(this.link)) {
            this.$router.push(utils.getRouteWithDefaultParams(this.link.name, 'params' in this.link ? this.link.params : {}, 'query' in this.link ? this.link.query : {}))
          } else {
            window.open(this.link, this.target)
          }
        }
      } else {
        if (this.link !== null) {
          if (this.$store.getters['config/configForKey']('app_config').config[0].defaults.showNoAccessModal) {
            const result = await accessControl.accessControlUrl(this.link)
            if (!result) {
              EventBus.$emit('noaccess', !result)
              return
            }
          }
        const token = this.user ? this.user.accessToken : null
        if(this.$store.getters['config/configForKey']('app_config').config[0].addTokenToEvaluationLink && this.link.includes('evaluations')){
          const url = this.link + '?jwttoken=' + token
          if(this.isApp) {
            this.openLink(url)
          } else {
            window.open(url, this.target)
          }
        } else{
          if(this.isApp && this.link.includes('http')) {
            this.openLink(this.link)

          } else {
            window.open(this.link, this.target)

          }
        }
          

        } else {
          this.$router.push(utils.getRouteWithDefaultParams('MyAreaPage', { module: moduleName.toLowerCase() }))
        }
        this.tracking(moduleName.toLowerCase())
      }
    },
    getModuleName(moduleKey) {
     
      if (Object.prototype.hasOwnProperty.call(moduleNames, moduleKey)) {
        return moduleNames[moduleKey]
      }
      return null
    },
    async tracking(moduleName) {
      await trackingApi.trackCustomAction('vmx-myareabox-action', null, moduleName)
    },
  },
}
</script>

<style lang="scss">
.my-area__box {
  background: #fff;
  height: 100%;
  border: 1px solid var(--primary);

  .card-body {
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: flex-start;
  }
}
.my-area__box-header {
  background: var(--primary);
  height: 75px;
  position: relative;
  overflow: hidden;

  svg {
    color: #fff;
    font-size: 5rem;
    position: absolute;
    right: 1rem;
    bottom: -1rem;
    opacity: .8;
  }
}
@media(max-width: 768px) {
  .my-area__box-header {
    height: 40px;

  svg {
    font-size: 1.75rem;
    top: 50%;
    transform: translateY(-50%);
    right: .5rem;
  }
}
}
.my-area__box-body {
  box-shadow: none;

  .card-body {
    padding: 1rem;
  }

  .my-area__box-title {
    font-weight: bold;
  }
}
</style>
